import React, { useState } from "react";
import "./FilterDashboard.scss";
import toast from "react-hot-toast";
import api from "components/api/api";
import { Spinner } from "reactstrap";

function FilterDashboard({ filters, setFilters, isAdmin }) {
  const [lowScorers, setLowScorers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const handleExcelExport = async () => {
    if (exportLoading) return;

    try {
      setExportLoading(true);
      await api.downloadOverallParticipationExcel();
    } catch (err) {
      console.error("Excel export failed", err);
      toast.error("Excel export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleSeeLowScorer = async () => {
    setLoading(true);
    setShowModal(true);
    setSelectedUsers([]);

    try {
      const response = await api.fetchLowScorers();
      const users = response.data.data || [];

      // Transform the API response to match the table structure
      const transformedUsers = users.map((user) => ({
        id: user.userId,
        staffId: user.staffId,
        username: user.username,
        region: user.region || "N/A",
        outlet: user.outlet || "N/A",
        avgPercentage: user.avgPercentage,
        attemptCount: user.attemptCount,
      }));

      setLowScorers(transformedUsers);
    } catch (error) {
      console.error("Error fetching low scorers:", error);
      toast.error("Failed to load low scorers");
      setLowScorers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((userId) => userId !== id)
        : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === lowScorers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(lowScorers.map((user) => user.id));
    }
  };

  const handleNotifyAll = async () => {
    // console.log("Selected Users:", selectedUsers);
    const payload = {
      quizSurveyId: null,
      message:
        "You have been identified as a low scorer. Please improve your performance.",
      targetUser: selectedUsers, // This matches the backend expectation
    };
    // console.log("Sending payload:", payload);

    try {
      const response = await api.notifyUsers(payload);
      // console.log("Notification sent:", response);
      toast.success("Notifications sent successfully!");
      setShowModal(false);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error sending notifications:", error);
      toast.error("Failed to send notifications");
    }
  };

  return (
    <div className="filter-dashboard">
      {/* Filters */}
      <input
        type="text"
        placeholder="🔍 Search by title..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="filter-input"
      />
      <select
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="filter-select"
      >
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="closed">Closed</option>
        <option value="expired">Expired</option>
      </select>
      <select
        value={filters.type}
        onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        className="filter-select"
      >
        <option value="">All Types</option>
        <option value="Quiz">Quiz</option>
        <option value="Survey">Survey</option>
      </select>
      <select
        value={filters.sort}
        onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
        className="filter-select"
      >
        <option value="">Sort by</option>
        <option value="latest">Latest</option>
        <option value="oldest">Oldest</option>
      </select>
      {!isAdmin && (
        <select
          value={filters.participation}
          className="filter-select"
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, participation: e.target.value }))
          }
        >
          <option value="">All</option>
          <option value="participated">Participated</option>
          <option value="notParticipated">Not Participated</option>
        </select>
      )}
      <input
        type="date"
        value={filters.date}
        onChange={(e) => setFilters({ ...filters, date: e.target.value })}
        className="filter-input"
      />
      {isAdmin && (
        <>
          <button className="low-scorer-btn" onClick={handleSeeLowScorer}>
            See Low Scorer
          </button>
          <button
            className="export-buttons-overall"
            onClick={handleExcelExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <Spinner size="sm" />
                Exporting...
              </>
            ) : (
              <>📊 Export Excel</>
            )}
          </button>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Low Scorers During Last 5 Weeks</h2>

            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner animation="border" role="status" />
                <p style={{ marginTop: "10px" }}>Loading low scorers...</p>
              </div>
            ) : lowScorers.length === 0 ? (
              <p style={{ textAlign: "center", padding: "20px" }}>
                No low scorers found
              </p>
            ) : (
              <>
                <table className="low-scorer-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            selectedUsers.length === lowScorers.length &&
                            lowScorers.length > 0
                          }
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Staff ID</th>
                      <th>Username</th>
                      <th>Region</th>
                      <th>Outlet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowScorers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleCheckboxChange(user.id)}
                          />
                        </td>
                        <td>{user.staffId}</td>
                        <td>{user.username}</td>
                        <td>{user.region}</td>
                        <td>{user.outlet}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div
                  style={{ display: "flex", gap: "10px", marginTop: "15px" }}
                >
                  <button
                    className="close-btn"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="notify-btn"
                    onClick={handleNotifyAll}
                    disabled={selectedUsers.length === 0}
                  >
                    Notify Selected ({selectedUsers.length})
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterDashboard;
